import React, { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";

// Send cookies with every request (needed for httpOnly adminSession cookie)
axios.defaults.withCredentials = true;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const firstPath = useRef(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Meta Pixel — fire PageView on SPA route changes (skip the first; pixel init already fired one). Consent-gated.
    if (firstPath.current) { firstPath.current = false; return; }
    if (window.fbq) window.fbq('track', 'PageView');
  }, [pathname]);

  return null;
};

// Anonymous visit counter. Counts EVERY page load as a visit (views) — even
// repeated entries from the same person — plus unique visitors per day
// (deduped server-side by a random first-party id). Skips admin pages. No PII.
// If the admin marked this browser as their own, visits are tagged `owner`
// so they can be subtracted in the dashboard.
const VisitTracker = () => {
  useEffect(() => {
    if (window.location.pathname.startsWith('/admin')) return;
    let vid = localStorage.getItem('mm_visitor_id');
    if (!vid) {
      vid = (window.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).slice(2)));
      localStorage.setItem('mm_visitor_id', vid);
    }
    const owner = localStorage.getItem('mm_owner') === '1' || localStorage.getItem('mm_ignore_visits') === '1';
    axios.post(`${API}/track/visit`, { visitor_id: vid, owner }).catch(() => {});
  }, []);
  return null;
};

// Pages
import LandingPage from "./pages/LandingPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import ContactPage from "./pages/ContactPage";
import DocumentPage from "./pages/DocumentPage";
import DownloadPage from "./pages/DownloadPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminManualPayments from "./pages/admin/AdminManualPayments";
import AdminTemplates from "./pages/admin/AdminTemplates";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminDocuments from "./pages/admin/AdminDocuments";
import AdminSocial from "./pages/admin/AdminSocial";
import AdminSends from "./pages/admin/AdminSends";
import AdminCMS from "./pages/admin/AdminCMS";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminPromoCodes from "./pages/admin/AdminPromoCodes";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminVisits from "./pages/admin/AdminVisits";
import CookieBanner from "./components/CookieBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cart Context
export const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart((prev) => {
      const exists = prev.find((item) => item.id === product.id);
      if (exists) return prev;
      return [...prev, { ...product, quantity: 1 }];
    });
    // Meta Pixel — AddToCart (consent-gated; value + currency only, no product name)
    if (window.fbq) {
      const value = Number(product.is_on_sale ? product.sale_price : product.regular_price) || 0;
      window.fbq('track', 'AddToCart', { value, currency: 'PLN' });
    }
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((sum, item) => {
    const price = Number(item.is_on_sale ? item.sale_price : item.regular_price) || 0;
    const qty = Number(item.quantity) || 1;
    return sum + price * qty;
  }, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, cartTotal }}>
      {children}
    </CartContext.Provider>
  );
};

// Auth Context for Admin
export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Token is kept in memory only — never in localStorage (XSS protection)
  // The httpOnly cookie handles persistence across page refreshes
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    // Global 401 interceptor — redirect to login on expired/invalid session
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401 && window.location.pathname.startsWith('/admin/')) {
          sessionStorage.removeItem('adminToken');
          setToken(null);
          setIsAuthenticated(false);
          window.location.href = '/admin';
        }
        return Promise.reject(err);
      }
    );

    // On mount, verify session via cookie or sessionStorage fallback
    const verifySession = async () => {
      try {
        const res = await axios.get(`${API}/auth/verify`);
        setToken(res.data.token);
        sessionStorage.setItem('adminToken', res.data.token);
        setIsAuthenticated(true);
      } catch {
        // Cookie failed — try sessionStorage fallback (same browser tab/session)
        const saved = sessionStorage.getItem('adminToken');
        if (saved) {
          try {
            const res = await axios.get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${saved}` } });
            setToken(res.data.token);
            sessionStorage.setItem('adminToken', res.data.token);
            setIsAuthenticated(true);
          } catch {
            sessionStorage.removeItem('adminToken');
            setToken(null);
            setIsAuthenticated(false);
          }
        } else {
          setToken(null);
          setIsAuthenticated(false);
        }
      }
      setAuthLoading(false);
    };
    verifySession();

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = (newToken) => {
    setToken(newToken);
    sessionStorage.setItem('adminToken', newToken);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    sessionStorage.removeItem('adminToken');
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, authLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route for admin pages — prevents rendering admin UI before auth check completes
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, authLoading } = useAuth();
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#10b981] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/admin" replace />;
  return children;
};

// Settings Context
export const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    site_name: "MrMinds",
    site_logo: "",
    site_logo_type: "text"
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/public`);
        setSettings(res.data);
      } catch (e) {
        console.error("Failed to load settings");
      }
    };
    fetchSettings();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <VisitTracker />
            <CookieBanner />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/kontakt" element={<ContactPage />} />
              <Route path="/dokument/:slug" element={<DocumentPage />} />
              <Route path="/download/:token" element={<DownloadPage />} />
              
              {/* Admin routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/produkty" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
              <Route path="/admin/zamowienia" element={<ProtectedRoute><AdminOrders /></ProtectedRoute>} />
              <Route path="/admin/przelewy" element={<ProtectedRoute><AdminManualPayments /></ProtectedRoute>} />
              <Route path="/admin/kontakty" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
              <Route path="/admin/szablony" element={<ProtectedRoute><AdminTemplates /></ProtectedRoute>} />
              <Route path="/admin/wysylki" element={<ProtectedRoute><AdminSends /></ProtectedRoute>} />
              <Route path="/admin/social" element={<ProtectedRoute><AdminSocial /></ProtectedRoute>} />
              <Route path="/admin/dokumenty" element={<ProtectedRoute><AdminDocuments /></ProtectedRoute>} />
              <Route path="/admin/ustawienia" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/cms" element={<ProtectedRoute><AdminCMS /></ProtectedRoute>} />
              <Route path="/admin/opinie" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
              <Route path="/admin/promocje" element={<ProtectedRoute><AdminPromoCodes /></ProtectedRoute>} />
              <Route path="/admin/analityka" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
              <Route path="/admin/odwiedziny" element={<ProtectedRoute><AdminVisits /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
